import type { Connection } from '@xyflow/react';
import type { CustomEdge } from '@/types/edge-types';
import { ConnectionType } from '@/types/edge-types';
import type { CustomNode, NodeParam, NodeStruct } from '@/types/node-types';

export const SWITCH_NODE_ID = 'switch_node';
export const VALIDATE_NODE_ID = 'validate_node';
export const SWITCH_CAPABILITY = 'workflow.switch.v1';
export const VALIDATE_CAPABILITY = 'workflow.validate.v1';
export const CONDITIONAL_EDGE_CAPABILITY = 'workflow.conditional_edges.v1';
export const BRANCH_GATING_CAPABILITY = 'workflow.branch_gating';

export const MAX_SWITCH_CASES = 128;
export const MAX_SWITCH_CONFIG_BYTES = 262_144;
export const MAX_VALIDATION_ASSERTIONS = 64;
export const MAX_VALIDATION_CONFIG_BYTES = 262_144;
export const MAX_VALIDATION_SCHEMA_BYTES = 65_536;
export const MAX_VALIDATION_SCHEMA_DEPTH = 16;
export const MAX_VALIDATION_SCHEMA_NODES = 512;
export const MAX_VALIDATION_PROPERTIES = 256;
export const MAX_VALIDATION_MESSAGE_LENGTH = 512;
export const MAX_SCHEMA_KEY_LENGTH = 128;
export const MAX_EXPRESSION_DEPTH = 16;
export const MAX_EXPRESSION_NODES = 128;
export const MAX_PATH_SEGMENTS = 32;
export const MAX_LITERAL_BYTES = 16_384;
export const MAX_LITERAL_COLLECTION_ITEMS = 256;
export const MAX_LITERAL_CONTAINER_DEPTH = 8;

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue =
  | JsonPrimitive
  | JsonValue[]
  | { [key: string]: JsonValue };

export type ExpressionAst =
  | { op: 'literal'; value: JsonValue }
  | { op: 'path'; path: Array<string | number> }
  | { op: 'not'; arg: ExpressionAst }
  | { op: 'and' | 'or'; args: ExpressionAst[] }
  | {
      op: 'eq' | 'ne' | 'lt' | 'lte' | 'gt' | 'gte';
      left: ExpressionAst;
      right: ExpressionAst;
    }
  | {
      op: 'in' | 'not_in';
      item: ExpressionAst;
      collection: ExpressionAst;
    }
  | { op: 'exists'; path: { op: 'path'; path: Array<string | number> } }
  | { op: 'is_null'; arg: ExpressionAst };

export interface SwitchCase {
  label: string;
  when: ExpressionAst;
}

export interface ValidationAssertion {
  code: string;
  message: string;
  path: Array<string | number>;
  when: ExpressionAst;
}

export interface ValidationIssue {
  code: string;
  path: Array<string | number>;
  message: string;
  source: 'schema' | 'assertion';
  keyword: string;
}

export interface ControlFlowValidationError {
  code: string;
  message: string;
  path: string;
}

export interface SwitchConfigValue {
  cases: SwitchCase[];
  defaultLabel: string | null;
  nullBehavior: 'error' | 'default';
}

export interface ValidateConfigValue {
  schema: Record<string, JsonValue> | null;
  assertions: ValidationAssertion[];
  mode: 'fail' | 'collect';
  errorCode: string;
  errorMessage: string;
}

const IDENTIFIER_PATTERN = /^[A-Za-z][A-Za-z0-9_.-]*$/;
const BRANCH_HANDLE_PREFIX = 'control-branch:';
const JSON_TYPES = new Set([
  'null',
  'boolean',
  'integer',
  'number',
  'string',
  'array',
  'object',
]);
const SCHEMA_KEYWORDS = new Set([
  'type',
  'enum',
  'const',
  'properties',
  'required',
  'additionalProperties',
  'minProperties',
  'maxProperties',
  'items',
  'minItems',
  'maxItems',
  'minLength',
  'maxLength',
  'minimum',
  'maximum',
  'exclusiveMinimum',
  'exclusiveMaximum',
]);
const FORBIDDEN_PROPERTY_NAMES = new Set([
  '__proto__',
  'constructor',
  'prototype',
]);

const isIdentifier = (value: string): boolean =>
  value.length <= MAX_SCHEMA_KEY_LENGTH && IDENTIFIER_PATTERN.test(value);

const hasUnsafeControlCharacter = (value: string): boolean =>
  [...value].some((character) => {
    const code = character.charCodeAt(0);
    return (
      code <= 8 ||
      code === 11 ||
      code === 12 ||
      (code >= 14 && code <= 31) ||
      code === 127
    );
  });

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const byteLength = (value: unknown): number => {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
};

const safeCanonicalJson = (value: unknown): string => {
  try {
    const normalize = (candidate: unknown): unknown => {
      if (Array.isArray(candidate)) return candidate.map(normalize);
      const record = asRecord(candidate);
      if (!record) return candidate;
      return Object.fromEntries(
        Object.keys(record)
          .sort()
          .map((key) => [key, normalize(record[key])]),
      );
    };
    return JSON.stringify(normalize(value));
  } catch {
    return '';
  }
};

const parseJsonParam = <T>(value: unknown, fallback: T): T => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return (value as T) ?? fallback;
};

export const getParam = (
  struct: NodeStruct,
  fieldId: string,
): NodeParam | undefined =>
  struct.params?.find((param) => param.field_id === fieldId);

export const getParamValue = (struct: NodeStruct, fieldId: string): unknown =>
  getParam(struct, fieldId)?.value;

export const getSwitchConfig = (struct: NodeStruct): SwitchConfigValue => {
  const rawCases = parseJsonParam<unknown[]>(
    getParamValue(struct, 'cases'),
    [],
  );
  const cases = Array.isArray(rawCases)
    ? rawCases
        .map((candidate) => {
          const record = asRecord(candidate);
          if (!record || typeof record.label !== 'string' || !record.when) {
            return null;
          }
          return {
            label: record.label,
            when: record.when as ExpressionAst,
          };
        })
        .filter((candidate): candidate is SwitchCase => candidate !== null)
    : [];
  const rawDefault = getParamValue(struct, 'default_label');
  const rawNullBehavior = getParamValue(struct, 'null_behavior');
  return {
    cases,
    defaultLabel:
      typeof rawDefault === 'string' && rawDefault.trim() ? rawDefault : null,
    nullBehavior: rawNullBehavior === 'default' ? 'default' : 'error',
  };
};

export const getValidateConfig = (struct: NodeStruct): ValidateConfigValue => {
  const schema = parseJsonParam<Record<string, JsonValue> | null>(
    getParamValue(struct, 'schema'),
    null,
  );
  const rawAssertions = parseJsonParam<unknown[]>(
    getParamValue(struct, 'assertions'),
    [],
  );
  const assertions = Array.isArray(rawAssertions)
    ? rawAssertions
        .map((candidate) => {
          const record = asRecord(candidate);
          if (
            !record ||
            typeof record.code !== 'string' ||
            typeof record.message !== 'string' ||
            !record.when
          ) {
            return null;
          }
          return {
            code: record.code,
            message: record.message,
            path: Array.isArray(record.path)
              ? (record.path as Array<string | number>)
              : [],
            when: record.when as ExpressionAst,
          };
        })
        .filter(
          (candidate): candidate is ValidationAssertion => candidate !== null,
        )
    : [];
  const mode = getParamValue(struct, 'mode');
  const errorCode = getParamValue(struct, 'error_code');
  const errorMessage = getParamValue(struct, 'error_message');
  return {
    schema: asRecord(schema) as Record<string, JsonValue> | null,
    assertions,
    mode: mode === 'collect' ? 'collect' : 'fail',
    errorCode:
      typeof errorCode === 'string' && errorCode
        ? errorCode
        : 'validation.failed',
    errorMessage:
      typeof errorMessage === 'string' && errorMessage
        ? errorMessage
        : 'Validation failed',
  };
};

const pushError = (
  errors: ControlFlowValidationError[],
  code: string,
  path: string,
  message: string,
) => errors.push({ code, path, message });

const exactKeys = (
  record: Record<string, unknown>,
  allowed: string[],
): boolean => Object.keys(record).every((key) => allowed.includes(key));

const validatePath = (
  value: unknown,
  path: string,
  errors: ControlFlowValidationError[],
  requireOne = true,
) => {
  if (
    !Array.isArray(value) ||
    (requireOne && value.length === 0) ||
    value.length > MAX_PATH_SEGMENTS
  ) {
    pushError(
      errors,
      'path_invalid',
      path,
      `Path must contain ${requireOne ? '1-' : '0-'}${MAX_PATH_SEGMENTS} segments.`,
    );
    return;
  }
  value.forEach((segment, index) => {
    const validString =
      typeof segment === 'string' &&
      segment.length > 0 &&
      segment.length <= MAX_SCHEMA_KEY_LENGTH;
    const validIndex =
      Number.isInteger(segment) &&
      typeof segment === 'number' &&
      segment >= 0 &&
      segment <= 100_000;
    if (!validString && !validIndex) {
      pushError(
        errors,
        'path_segment_invalid',
        `${path}[${index}]`,
        'Path segments must be bounded strings or non-negative indexes.',
      );
    }
  });
};

const validateJsonLiteral = (
  value: unknown,
  path: string,
  depth: number,
  errors: ControlFlowValidationError[],
) => {
  if (depth > MAX_LITERAL_CONTAINER_DEPTH) {
    pushError(
      errors,
      'literal_depth',
      path,
      `JSON container depth exceeds ${MAX_LITERAL_CONTAINER_DEPTH}.`,
    );
    return;
  }
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value))
  ) {
    return;
  }
  if (Array.isArray(value)) {
    if (value.length > MAX_LITERAL_COLLECTION_ITEMS) {
      pushError(
        errors,
        'literal_items',
        path,
        `JSON arrays are limited to ${MAX_LITERAL_COLLECTION_ITEMS} items.`,
      );
    }
    value.forEach((item, index) =>
      validateJsonLiteral(item, `${path}[${index}]`, depth + 1, errors),
    );
    return;
  }
  const record = asRecord(value);
  if (record) {
    if (Object.keys(record).length > MAX_LITERAL_COLLECTION_ITEMS) {
      pushError(
        errors,
        'literal_properties',
        path,
        `JSON objects are limited to ${MAX_LITERAL_COLLECTION_ITEMS} properties.`,
      );
    }
    Object.entries(record).forEach(([key, item]) => {
      if (!key || key.length > MAX_SCHEMA_KEY_LENGTH) {
        pushError(
          errors,
          'literal_key',
          path,
          `JSON object keys must contain 1-${MAX_SCHEMA_KEY_LENGTH} characters.`,
        );
      }
      validateJsonLiteral(item, `${path}.${key}`, depth + 1, errors);
    });
    return;
  }
  pushError(
    errors,
    'literal_type',
    path,
    'Expression literals must be JSON values.',
  );
};

export const validateExpressionAst = (
  value: unknown,
  rootPath = 'expression',
): ControlFlowValidationError[] => {
  const errors: ControlFlowValidationError[] = [];
  let nodeCount = 0;
  let literalBytes = 0;
  const seen = new WeakSet<object>();

  const walk = (candidate: unknown, path: string, depth: number) => {
    const record = asRecord(candidate);
    if (!record || typeof record.op !== 'string') {
      pushError(
        errors,
        'expression_shape',
        path,
        'Expression nodes must be JSON objects with an op.',
      );
      return;
    }
    if (seen.has(record)) {
      pushError(errors, 'expression_cycle', path, 'Expression is cyclic.');
      return;
    }
    seen.add(record);
    nodeCount += 1;
    if (nodeCount > MAX_EXPRESSION_NODES) {
      pushError(
        errors,
        'expression_nodes',
        path,
        `Expressions are limited to ${MAX_EXPRESSION_NODES} nodes.`,
      );
      return;
    }
    if (depth > MAX_EXPRESSION_DEPTH) {
      pushError(
        errors,
        'expression_depth',
        path,
        `Expression depth is limited to ${MAX_EXPRESSION_DEPTH}.`,
      );
      return;
    }

    switch (record.op) {
      case 'literal':
        if (!exactKeys(record, ['op', 'value']) || !('value' in record)) {
          pushError(
            errors,
            'expression_fields',
            path,
            'Invalid literal fields.',
          );
          return;
        }
        validateJsonLiteral(record.value, `${path}.value`, 0, errors);
        literalBytes += byteLength(record.value);
        if (literalBytes > MAX_LITERAL_BYTES) {
          pushError(
            errors,
            'literal_bytes',
            path,
            `Expression literal payload exceeds ${MAX_LITERAL_BYTES} bytes.`,
          );
        }
        return;
      case 'path':
        if (!exactKeys(record, ['op', 'path'])) {
          pushError(errors, 'expression_fields', path, 'Invalid path fields.');
          return;
        }
        validatePath(record.path, `${path}.path`, errors);
        return;
      case 'not':
      case 'is_null':
        if (!exactKeys(record, ['op', 'arg'])) {
          pushError(errors, 'expression_fields', path, 'Invalid unary fields.');
          return;
        }
        walk(record.arg, `${path}.arg`, depth + 1);
        return;
      case 'and':
      case 'or':
        if (
          !exactKeys(record, ['op', 'args']) ||
          !Array.isArray(record.args) ||
          record.args.length < 1 ||
          record.args.length > 32
        ) {
          pushError(
            errors,
            'expression_args',
            path,
            'Boolean expressions require 1-32 arguments.',
          );
          return;
        }
        record.args.forEach((arg, index) =>
          walk(arg, `${path}.args[${index}]`, depth + 1),
        );
        return;
      case 'eq':
      case 'ne':
      case 'lt':
      case 'lte':
      case 'gt':
      case 'gte':
        if (!exactKeys(record, ['op', 'left', 'right'])) {
          pushError(
            errors,
            'expression_fields',
            path,
            'Invalid comparison fields.',
          );
          return;
        }
        walk(record.left, `${path}.left`, depth + 1);
        walk(record.right, `${path}.right`, depth + 1);
        return;
      case 'in':
      case 'not_in':
        if (!exactKeys(record, ['op', 'item', 'collection'])) {
          pushError(
            errors,
            'expression_fields',
            path,
            'Invalid membership fields.',
          );
          return;
        }
        walk(record.item, `${path}.item`, depth + 1);
        walk(record.collection, `${path}.collection`, depth + 1);
        return;
      case 'exists': {
        if (!exactKeys(record, ['op', 'path'])) {
          pushError(
            errors,
            'expression_fields',
            path,
            'Invalid exists fields.',
          );
          return;
        }
        const pathExpression = asRecord(record.path);
        nodeCount += 1;
        if (
          nodeCount > MAX_EXPRESSION_NODES ||
          depth + 1 > MAX_EXPRESSION_DEPTH
        ) {
          pushError(
            errors,
            'expression_limits',
            `${path}.path`,
            'Exists path exceeds expression production limits.',
          );
          return;
        }
        if (
          !pathExpression ||
          pathExpression.op !== 'path' ||
          !exactKeys(pathExpression, ['op', 'path'])
        ) {
          pushError(
            errors,
            'expression_path',
            `${path}.path`,
            'Exists requires a path expression.',
          );
          return;
        }
        validatePath(pathExpression.path, `${path}.path.path`, errors);
        return;
      }
      default:
        pushError(
          errors,
          'expression_op',
          `${path}.op`,
          `Unsupported expression operator "${record.op}".`,
        );
    }
  };

  walk(value, rootPath, 1);
  return errors;
};

export const validateSwitchConfig = (
  config: SwitchConfigValue,
): ControlFlowValidationError[] => {
  const errors: ControlFlowValidationError[] = [];
  if (
    !Array.isArray(config.cases) ||
    config.cases.length < 1 ||
    config.cases.length > MAX_SWITCH_CASES
  ) {
    pushError(
      errors,
      'switch_case_count',
      'cases',
      `Switch requires 1-${MAX_SWITCH_CASES} cases.`,
    );
  }
  const labels = new Set<string>();
  config.cases.forEach((switchCase, index) => {
    if (!isIdentifier(switchCase.label)) {
      pushError(
        errors,
        'switch_label',
        `cases[${index}].label`,
        'Labels must start with a letter and contain only letters, numbers, ".", "_" or "-".',
      );
    } else if (labels.has(switchCase.label)) {
      pushError(
        errors,
        'switch_label_duplicate',
        `cases[${index}].label`,
        'Case labels must be unique.',
      );
    }
    labels.add(switchCase.label);
    errors.push(
      ...validateExpressionAst(switchCase.when, `cases[${index}].when`),
    );
  });
  if (config.defaultLabel !== null && !isIdentifier(config.defaultLabel)) {
    pushError(
      errors,
      'switch_default_label',
      'default_label',
      'The default label must be a valid identifier.',
    );
  }
  if (config.defaultLabel && labels.has(config.defaultLabel)) {
    pushError(
      errors,
      'switch_default_duplicate',
      'default_label',
      'The default label must differ from every case label.',
    );
  }
  if (config.nullBehavior === 'default' && !config.defaultLabel) {
    pushError(
      errors,
      'switch_null_default',
      'null_behavior',
      'Null-to-default behavior requires a default label.',
    );
  }
  if (
    byteLength({
      config_version: '1.0',
      cases: config.cases,
      default_label: config.defaultLabel,
      null_behavior: config.nullBehavior,
    }) > MAX_SWITCH_CONFIG_BYTES
  ) {
    pushError(
      errors,
      'switch_config_bytes',
      'config',
      `Switch configuration exceeds ${MAX_SWITCH_CONFIG_BYTES} bytes.`,
    );
  }
  return errors;
};

export const validateSchemaSubset = (
  schema: unknown,
): ControlFlowValidationError[] => {
  const errors: ControlFlowValidationError[] = [];
  let nodeCount = 0;
  if (byteLength(schema) > MAX_VALIDATION_SCHEMA_BYTES) {
    pushError(
      errors,
      'schema_bytes',
      'schema',
      `Schema exceeds ${MAX_VALIDATION_SCHEMA_BYTES} bytes.`,
    );
  }
  const walk = (candidate: unknown, path: string, depth: number) => {
    const record = asRecord(candidate);
    if (!record) {
      pushError(
        errors,
        'schema_shape',
        path,
        'Every schema node must be a JSON object.',
      );
      return;
    }
    nodeCount += 1;
    if (nodeCount > MAX_VALIDATION_SCHEMA_NODES) {
      pushError(
        errors,
        'schema_nodes',
        path,
        `Schema exceeds ${MAX_VALIDATION_SCHEMA_NODES} nodes.`,
      );
      return;
    }
    if (depth > MAX_VALIDATION_SCHEMA_DEPTH) {
      pushError(
        errors,
        'schema_depth',
        path,
        `Schema exceeds depth ${MAX_VALIDATION_SCHEMA_DEPTH}.`,
      );
      return;
    }
    if (Object.keys(record).length > MAX_VALIDATION_PROPERTIES) {
      pushError(
        errors,
        'schema_keywords',
        path,
        'Schema node has too many keywords.',
      );
    }
    Object.keys(record).forEach((key) => {
      if (
        !key ||
        key.length > MAX_SCHEMA_KEY_LENGTH ||
        !SCHEMA_KEYWORDS.has(key)
      ) {
        pushError(
          errors,
          'schema_keyword',
          `${path}.${key}`,
          `Unsupported schema keyword "${key}".`,
        );
      }
    });
    const declaredType = record.type;
    if (
      declaredType !== undefined &&
      (typeof declaredType !== 'string' || !JSON_TYPES.has(declaredType))
    ) {
      pushError(
        errors,
        'schema_type',
        `${path}.type`,
        'Schema type must be one supported JSON type.',
      );
    }
    const boundedIntegerKeys = [
      'minProperties',
      'maxProperties',
      'minItems',
      'maxItems',
      'minLength',
      'maxLength',
    ];
    boundedIntegerKeys.forEach((key) => {
      const value = record[key];
      if (
        value !== undefined &&
        (!Number.isInteger(value) ||
          typeof value !== 'number' ||
          value < 0 ||
          value > MAX_VALIDATION_PROPERTIES)
      ) {
        pushError(
          errors,
          'schema_bound',
          `${path}.${key}`,
          `${key} must be an integer from 0-${MAX_VALIDATION_PROPERTIES}.`,
        );
      }
    });
    [
      ['minProperties', 'maxProperties'],
      ['minItems', 'maxItems'],
      ['minLength', 'maxLength'],
      ['minimum', 'maximum'],
    ].forEach(([lower, upper]) => {
      if (
        typeof record[lower] === 'number' &&
        typeof record[upper] === 'number' &&
        record[lower] > record[upper]
      ) {
        pushError(
          errors,
          'schema_range',
          path,
          `${lower} must not exceed ${upper}.`,
        );
      }
    });
    ['minimum', 'maximum', 'exclusiveMinimum', 'exclusiveMaximum'].forEach(
      (key) => {
        if (
          record[key] !== undefined &&
          (typeof record[key] !== 'number' || !Number.isFinite(record[key]))
        ) {
          pushError(
            errors,
            'schema_number',
            `${path}.${key}`,
            `${key} must be a finite number.`,
          );
        }
      },
    );
    if (
      'additionalProperties' in record &&
      typeof record.additionalProperties !== 'boolean'
    ) {
      pushError(
        errors,
        'schema_additional_properties',
        `${path}.additionalProperties`,
        'additionalProperties must be boolean.',
      );
    }
    const properties = asRecord(record.properties);
    if (record.properties !== undefined && !properties) {
      pushError(
        errors,
        'schema_properties',
        `${path}.properties`,
        'properties must be an object.',
      );
    }
    if (properties) {
      if (Object.keys(properties).length > MAX_VALIDATION_PROPERTIES) {
        pushError(
          errors,
          'schema_properties',
          `${path}.properties`,
          'properties exceeds the production limit.',
        );
      }
      Object.entries(properties).forEach(([name, child]) => {
        if (
          !name ||
          name.length > MAX_SCHEMA_KEY_LENGTH ||
          FORBIDDEN_PROPERTY_NAMES.has(name)
        ) {
          pushError(
            errors,
            'schema_property_name',
            `${path}.properties`,
            `Unsupported property name "${name}".`,
          );
        }
        walk(child, `${path}.properties.${name}`, depth + 1);
      });
    }
    if (record.items !== undefined) {
      walk(record.items, `${path}.items`, depth + 1);
    }
    if (record.required !== undefined) {
      const required = record.required;
      if (
        !Array.isArray(required) ||
        required.length > MAX_VALIDATION_PROPERTIES ||
        required.some((name) => typeof name !== 'string' || !name) ||
        new Set(required).size !== required.length
      ) {
        pushError(
          errors,
          'schema_required',
          `${path}.required`,
          'required must be a bounded array of unique non-empty strings.',
        );
      } else if (
        properties &&
        required.some((name) => !((name as string) in properties))
      ) {
        pushError(
          errors,
          'schema_required',
          `${path}.required`,
          'Required fields must be declared in properties.',
        );
      }
    }
    if (record.enum !== undefined) {
      if (
        !Array.isArray(record.enum) ||
        record.enum.length < 1 ||
        record.enum.length > MAX_VALIDATION_PROPERTIES
      ) {
        pushError(
          errors,
          'schema_enum',
          `${path}.enum`,
          'enum must contain 1-256 JSON values.',
        );
      } else {
        const seenValues = new Set<string>();
        record.enum.forEach((item, index) => {
          validateJsonLiteral(item, `${path}.enum[${index}]`, 0, errors);
          const canonical = `${item === null ? 'null' : typeof item}:${safeCanonicalJson(item)}`;
          if (seenValues.has(canonical)) {
            pushError(
              errors,
              'schema_enum_duplicate',
              `${path}.enum[${index}]`,
              'enum values must be unique.',
            );
          }
          seenValues.add(canonical);
        });
      }
    }
    if (record.const !== undefined) {
      validateJsonLiteral(record.const, `${path}.const`, 0, errors);
    }
    if (typeof declaredType === 'string') {
      const keywordGroups: Array<[Set<string>, string]> = [
        [
          new Set([
            'properties',
            'required',
            'additionalProperties',
            'minProperties',
            'maxProperties',
          ]),
          'object',
        ],
        [new Set(['items', 'minItems', 'maxItems']), 'array'],
        [new Set(['minLength', 'maxLength']), 'string'],
        [
          new Set([
            'minimum',
            'maximum',
            'exclusiveMinimum',
            'exclusiveMaximum',
          ]),
          'number',
        ],
      ];
      keywordGroups.forEach(([keywords, expectedType]) => {
        const usesGroup = Object.keys(record).some((key) => keywords.has(key));
        const compatible =
          declaredType === expectedType ||
          (expectedType === 'number' && declaredType === 'integer');
        if (usesGroup && !compatible) {
          pushError(
            errors,
            'schema_type_keyword',
            path,
            `Schema keywords are incompatible with type "${declaredType}".`,
          );
        }
      });
    }
  };
  walk(schema, 'schema', 0);
  return errors;
};

export const validateValidateConfig = (
  config: ValidateConfigValue,
): ControlFlowValidationError[] => {
  const errors: ControlFlowValidationError[] = [];
  if (!config.schema && config.assertions.length === 0) {
    pushError(
      errors,
      'validation_empty',
      'config',
      'Provide a schema and/or at least one assertion.',
    );
  }
  if (config.schema) errors.push(...validateSchemaSubset(config.schema));
  if (config.assertions.length > MAX_VALIDATION_ASSERTIONS) {
    pushError(
      errors,
      'assertion_count',
      'assertions',
      `Assertions are limited to ${MAX_VALIDATION_ASSERTIONS}.`,
    );
  }
  const codes = new Set<string>();
  config.assertions.forEach((assertion, index) => {
    if (!isIdentifier(assertion.code)) {
      pushError(
        errors,
        'assertion_code',
        `assertions[${index}].code`,
        'Assertion code must be a valid identifier.',
      );
    } else if (codes.has(assertion.code)) {
      pushError(
        errors,
        'assertion_code_duplicate',
        `assertions[${index}].code`,
        'Assertion codes must be unique.',
      );
    }
    codes.add(assertion.code);
    if (
      !assertion.message.trim() ||
      assertion.message.length > MAX_VALIDATION_MESSAGE_LENGTH ||
      hasUnsafeControlCharacter(assertion.message)
    ) {
      pushError(
        errors,
        'assertion_message',
        `assertions[${index}].message`,
        `Messages must contain 1-${MAX_VALIDATION_MESSAGE_LENGTH} characters.`,
      );
    }
    validatePath(assertion.path, `assertions[${index}].path`, errors, false);
    errors.push(
      ...validateExpressionAst(assertion.when, `assertions[${index}].when`),
    );
  });
  if (!isIdentifier(config.errorCode)) {
    pushError(
      errors,
      'validation_error_code',
      'error_code',
      'Failure code must be a valid identifier.',
    );
  }
  if (
    !config.errorMessage.trim() ||
    config.errorMessage.length > MAX_VALIDATION_MESSAGE_LENGTH ||
    hasUnsafeControlCharacter(config.errorMessage)
  ) {
    pushError(
      errors,
      'validation_error_message',
      'error_message',
      `Failure message must contain 1-${MAX_VALIDATION_MESSAGE_LENGTH} characters.`,
    );
  }
  if (
    byteLength({
      config_version: '1.0',
      schema: config.schema,
      assertions: config.assertions,
      mode: config.mode,
      error_code: config.errorCode,
      error_message: config.errorMessage,
    }) > MAX_VALIDATION_CONFIG_BYTES
  ) {
    pushError(
      errors,
      'validation_config_bytes',
      'config',
      `Validation configuration exceeds ${MAX_VALIDATION_CONFIG_BYTES} bytes.`,
    );
  }
  return errors;
};

const addCapabilityValues = (
  value: unknown,
  target: Set<string>,
  explicitlyDisabled?: Set<string>,
) => {
  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (typeof item === 'string') target.add(item);
    });
    return;
  }
  const record = asRecord(value);
  if (record) {
    Object.entries(record).forEach(([key, enabled]) => {
      if (enabled === true) target.add(key);
      if (enabled === false) explicitlyDisabled?.add(key);
    });
  }
};

/**
 * Reads only explicit capability advertisements. For a visible catalog item,
 * capability_identity and required_capabilities are part of the backend's
 * executable contract; nothing is inferred from a node ID or display name.
 */
export const extractAdvertisedCapabilities = (response: unknown): string[] => {
  const capabilities = new Set<string>();
  const explicitlyDisabled = new Set<string>();
  const root = asRecord(response);
  if (!root) return [];
  const data = asRecord(root.data);
  const hasExplicitCapabilityAdvertisement =
    root.capabilities !== undefined ||
    root.runtime_capabilities !== undefined ||
    data?.capabilities !== undefined ||
    data?.runtime_capabilities !== undefined;
  addCapabilityValues(root.capabilities, capabilities, explicitlyDisabled);
  addCapabilityValues(
    root.runtime_capabilities,
    capabilities,
    explicitlyDisabled,
  );
  if (data) {
    addCapabilityValues(data.capabilities, capabilities, explicitlyDisabled);
    addCapabilityValues(
      data.runtime_capabilities,
      capabilities,
      explicitlyDisabled,
    );
  }
  const catalog = Array.isArray(root.catalog)
    ? root.catalog
    : Array.isArray(data?.catalog)
      ? data.catalog
      : [];
  catalog.forEach((candidate) => {
    const node = asRecord(candidate);
    if (!node || node.show === false) return;
    if (typeof node.capability_identity === 'string') {
      capabilities.add(node.capability_identity);
    }
    if (!hasExplicitCapabilityAdvertisement) {
      addCapabilityValues(node.required_capabilities, capabilities);
    }
  });
  explicitlyDisabled.forEach((capability) => capabilities.delete(capability));
  return [...capabilities].sort();
};

export const hasCapability = (
  capabilities: readonly string[],
  capability: string,
): boolean => capabilities.includes(capability);

export const isCatalogNodeExecutable = (
  node: Pick<NodeStruct, 'show' | 'required_capabilities'>,
  capabilities: readonly string[],
): boolean =>
  node.show !== false &&
  (node.required_capabilities ?? []).every((capability) =>
    hasCapability(capabilities, capability),
  );

export const hasConditionalEdgeCapabilities = (
  capabilities: readonly string[],
): boolean =>
  hasCapability(capabilities, CONDITIONAL_EDGE_CAPABILITY) &&
  hasCapability(capabilities, BRANCH_GATING_CAPABILITY);

export const getBranchHandleId = (label: string): string =>
  `${BRANCH_HANDLE_PREFIX}${encodeURIComponent(label)}`;

export const getBranchLabelFromHandle = (
  handleId: string | null | undefined,
): string | null => {
  if (!handleId?.startsWith(BRANCH_HANDLE_PREFIX)) return null;
  try {
    return decodeURIComponent(handleId.slice(BRANCH_HANDLE_PREFIX.length));
  } catch {
    return null;
  }
};

export const getControlTargetHandleId = (nodeId: string): string => nodeId;

export const isConditionalEdge = (
  edge: Pick<CustomEdge, 'edge_type'>,
): boolean => edge.edge_type === ConnectionType.CONDITIONAL;

export const getConditionalBranchLabel = (
  edge: Pick<CustomEdge, 'data' | 'sourceHandle'>,
): string | null => {
  const data = asRecord(edge.data);
  return typeof data?.branch_label === 'string'
    ? data.branch_label
    : getBranchLabelFromHandle(edge.sourceHandle);
};

export const getSwitchBranchLabels = (struct: NodeStruct): string[] => {
  const config = getSwitchConfig(struct);
  return [
    ...config.cases.map((switchCase) => switchCase.label),
    ...(config.defaultLabel ? [config.defaultLabel] : []),
  ];
};

export const hasAttachedBranchEdge = (
  edges: readonly CustomEdge[],
  sourceNodeId: string,
  branchLabel: string,
): boolean =>
  edges.some(
    (edge) =>
      edge.source === sourceNodeId &&
      isConditionalEdge(edge) &&
      getConditionalBranchLabel(edge) === branchLabel,
  );

export const wouldOrphanConditionalEdge = (
  node: CustomNode,
  fieldId: string,
  nextValue: unknown,
  edges: readonly CustomEdge[],
): boolean => {
  if (node.data.struct.node_id !== SWITCH_NODE_ID) return false;
  const attachedLabels = new Set(
    edges
      .filter((edge) => edge.source === node.id && isConditionalEdge(edge))
      .map(getConditionalBranchLabel)
      .filter((label): label is string => Boolean(label)),
  );
  if (attachedLabels.size === 0) return false;
  const config = getSwitchConfig(node.data.struct);
  if (fieldId === 'cases') {
    const rawCases = parseJsonParam<unknown[]>(nextValue, []);
    const nextLabels = new Set(
      Array.isArray(rawCases)
        ? rawCases
            .map((candidate) => asRecord(candidate)?.label)
            .filter((label): label is string => typeof label === 'string')
        : [],
    );
    if (config.defaultLabel) nextLabels.add(config.defaultLabel);
    return [...attachedLabels].some((label) => !nextLabels.has(label));
  }
  if (fieldId === 'default_label') {
    const nextLabels = new Set(config.cases.map((item) => item.label));
    if (typeof nextValue === 'string' && nextValue) {
      nextLabels.add(nextValue);
    }
    return [...attachedLabels].some((label) => !nextLabels.has(label));
  }
  return false;
};

export interface ConditionalConnectionValidation {
  valid: boolean;
  branchLabel: string | null;
  reason?: string;
}

export const validateConditionalConnection = (
  connection: Connection,
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  capabilities: readonly string[],
  ignoredEdgeId?: string,
): ConditionalConnectionValidation => {
  const branchLabel = getBranchLabelFromHandle(connection.sourceHandle);
  if (!branchLabel) return { valid: false, branchLabel: null };
  if (!hasConditionalEdgeCapabilities(capabilities)) {
    return {
      valid: false,
      branchLabel,
      reason: 'Conditional-edge runtime capability is unavailable.',
    };
  }
  if (!connection.source || !connection.target) {
    return { valid: false, branchLabel, reason: 'Both nodes are required.' };
  }
  if (connection.source === connection.target) {
    return {
      valid: false,
      branchLabel,
      reason: 'A switch cannot branch to itself.',
    };
  }
  if (connection.targetHandle !== getControlTargetHandleId(connection.target)) {
    return {
      valid: false,
      branchLabel,
      reason: 'Conditional branches require a control input.',
    };
  }
  const source = nodes.find((node) => node.id === connection.source);
  const target = nodes.find((node) => node.id === connection.target);
  if (!source || !target || source.data.struct.node_id !== SWITCH_NODE_ID) {
    return {
      valid: false,
      branchLabel,
      reason: 'Conditional branches must originate from a Switch node.',
    };
  }
  if (!getSwitchBranchLabels(source.data.struct).includes(branchLabel)) {
    return {
      valid: false,
      branchLabel,
      reason: 'The branch label is no longer configured on this Switch.',
    };
  }
  const adjacency = new Map<string, string[]>();
  edges.forEach((edge) => {
    if (edge.id === ignoredEdgeId) return;
    adjacency.set(edge.source, [
      ...(adjacency.get(edge.source) ?? []),
      edge.target,
    ]);
  });
  const pending = [connection.target];
  const visited = new Set<string>();
  while (pending.length > 0) {
    const nodeId = pending.pop();
    if (!nodeId || visited.has(nodeId)) continue;
    if (nodeId === connection.source) {
      return {
        valid: false,
        branchLabel,
        reason: 'The conditional branch would create a workflow cycle.',
      };
    }
    visited.add(nodeId);
    pending.push(...(adjacency.get(nodeId) ?? []));
  }
  // One label may fan out to several targets (all execute in parallel when
  // selected) and one target may be reached from several labels; only an
  // exact duplicate (label, target) pair adds nothing and is rejected.
  const duplicateEdge = edges.some(
    (edge) =>
      edge.id !== ignoredEdgeId &&
      edge.source === connection.source &&
      isConditionalEdge(edge) &&
      getConditionalBranchLabel(edge) === branchLabel &&
      edge.target === connection.target,
  );
  if (duplicateEdge) {
    return {
      valid: false,
      branchLabel,
      reason: 'This branch label already connects to that target.',
    };
  }
  return { valid: true, branchLabel };
};

export const getSelectedCase = (result: unknown): string | null => {
  const root = asRecord(result);
  if (!root) return null;
  const selectedCaseOutput = asRecord(root.output_ports_data)?.selected_case;
  const candidates = [
    root.selected_case,
    root.selected_label,
    asRecord(root.metadata)?.selected_case,
    typeof selectedCaseOutput === 'string'
      ? selectedCaseOutput
      : asRecord(selectedCaseOutput)?.data,
    typeof root.output_data === 'string' ? root.output_data : undefined,
    asRecord(root.output_data)?.selected_case,
    asRecord(root.data)?.selected_case,
  ];
  return (
    candidates.find(
      (candidate): candidate is string =>
        typeof candidate === 'string' && Boolean(candidate),
    ) ?? null
  );
};

export interface NodeControlExecutionState {
  status: string | null;
  reason: string | null;
  skipped: boolean;
  selectedCase: string | null;
}

export const getNodeControlExecutionState = (
  result: unknown,
): NodeControlExecutionState => {
  const root = asRecord(result);
  const metadata = asRecord(root?.metadata);
  const statusCandidates = [
    root?.status,
    root?.node_result_status,
    metadata?.status,
  ];
  const status =
    statusCandidates.find(
      (candidate): candidate is string => typeof candidate === 'string',
    ) ?? null;
  const reasonCandidates = [
    root?.reason,
    root?.skip_reason,
    metadata?.reason,
    metadata?.skip_reason,
  ];
  const reason =
    reasonCandidates.find(
      (candidate): candidate is string => typeof candidate === 'string',
    ) ?? null;
  return {
    status,
    reason,
    skipped: status === 'skipped' || reason === 'skipped_by_switch',
    selectedCase: getSelectedCase(result),
  };
};

export const getValidationIssues = (result: unknown): ValidationIssue[] => {
  const root = asRecord(result);
  const candidates = [
    asRecord(root?.metadata)?.errors,
    root?.errors,
    asRecord(root?.data)?.errors,
  ];
  const issues = candidates.find(Array.isArray);
  if (!issues) return [];
  return issues
    .map((candidate) => {
      const issue = asRecord(candidate);
      if (
        !issue ||
        typeof issue.code !== 'string' ||
        typeof issue.message !== 'string' ||
        (issue.source !== 'schema' && issue.source !== 'assertion') ||
        typeof issue.keyword !== 'string'
      ) {
        return null;
      }
      return {
        code: issue.code,
        message: issue.message,
        source: issue.source,
        keyword: issue.keyword,
        path: Array.isArray(issue.path)
          ? (issue.path as Array<string | number>)
          : [],
      };
    })
    .filter((issue): issue is ValidationIssue => issue !== null);
};
